import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Heart, Shield, Users, Church, Mail, Instagram, Facebook, Linkedin, 
  Check, Loader2, LogOut, Download, Trash2, ChevronLeft, ChevronRight,
  BarChart3, Eye, MousePointer, FileText, TrendingUp, Users2, Copy, Share2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios
axios.defaults.withCredentials = true;

// ============ Auth Context ============
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = authenticated
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// ============ Analytics Tracking ============
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('yoked_session');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('yoked_session', sessionId);
  }
  return sessionId;
};

const trackEvent = async (eventType, data = {}) => {
  try {
    await axios.post(`${API}/analytics/event`, {
      event_type: eventType,
      session_id: getSessionId(),
      page: window.location.pathname,
      ...data
    });
  } catch (e) {
    console.error('Analytics error:', e);
  }
};

// ============ Protected Route ============
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-burgundy" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// ============ Infinity Logo ============
const InfinityLogo = ({ className = "" }) => (
  <div className={`infinity-logo ${className}`}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18.178 8c5.096 0 5.096 8 0 8-5.095 0-7.133-8-12.739-8-4.585 0-4.585 8 0 8 5.606 0 7.644-8 12.739-8z" />
    </svg>
  </div>
);

// ============ Header ============
const Header = () => {
  const scrollToWaitlist = () => {
    document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
    trackEvent('cta_click', { element_id: 'header-cta' });
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-cream/90 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <InfinityLogo />
          <span className="font-cta text-burgundy text-xl font-semibold tracking-wide">YOKED</span>
        </div>
        <button 
          onClick={scrollToWaitlist}
          className="btn-primary text-sm py-3 px-6"
          data-testid="header-cta-btn"
        >
          Join the Waitlist
        </button>
      </div>
    </header>
  );
};

// ============ Hero Section ============
const HeroSection = () => {
  const scrollToWaitlist = () => {
    document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
    trackEvent('cta_click', { element_id: 'hero-cta' });
  };

  return (
    <section className="hero-section flex items-center justify-center pt-24" data-testid="hero-section">
      <div className="max-w-4xl mx-auto text-center px-6 py-16">
        <h1 
          className="font-display hero-headline text-burgundy mb-6 animate-fade-in-up opacity-0"
          style={{ fontSize: '64px', lineHeight: '1.1', animationDelay: '0.1s', animationFillMode: 'forwards' }}
          data-testid="hero-headline"
        >
          Find Your Kingdom Partner
        </h1>
        
        <p 
          className="font-body hero-subheadline text-dark-burgundy max-w-3xl mx-auto mb-8 animate-fade-in-up opacity-0"
          style={{ fontSize: '18px', lineHeight: '1.8', animationDelay: '0.4s', animationFillMode: 'forwards' }}
          data-testid="hero-subheadline"
        >
          Yoked is a faith-vetted Christian courtship platform for active believers seeking intentional, Christ-centred relationships that lead toward marriage.
        </p>
        
        <p 
          className="font-body text-dark-burgundy/80 max-w-2xl mx-auto mb-10 animate-fade-in-up opacity-0"
          style={{ fontSize: '16px', lineHeight: '1.7', animationDelay: '0.5s', animationFillMode: 'forwards' }}
        >
          Whether you're looking for a partner to grow with spiritually, serve alongside, or build a God-centred family, Yoked helps you connect with someone who shares your faith, values, and calling.
        </p>
        
        <div className="animate-fade-in-up opacity-0" style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}>
          <button 
            onClick={scrollToWaitlist}
            className="btn-primary text-lg py-4 px-10"
            data-testid="hero-cta-btn"
          >
            Join the Waitlist
          </button>
        </div>
      </div>
    </section>
  );
};

// ============ About Section ============
const AboutSection = () => {
  const steps = [
    { number: 1, text: "Complete a short faith-fit application" },
    { number: 2, text: "Get approved if you align with our kingdom-focused values" },
    { number: 3, text: "Create your profile and connect with intentional, Christ-centered singles" },
    { number: 4, text: "Build meaningful relationships based on shared faith and life purpose" }
  ];

  return (
    <section className="about-section" id="about" data-testid="about-section">
      <div className="max-w-5xl mx-auto">
        <p className="font-cta text-burgundy/60 text-sm uppercase tracking-widest text-center mb-4" data-testid="how-it-works-label">
          How It Works
        </p>
        <h2 className="font-display section-heading text-burgundy text-center mb-12" style={{ fontSize: '36px' }} data-testid="about-heading">
          Intentional. Faith-Focused. Kingdom-Aligned.
        </h2>
        
        <div className="space-y-8 max-w-2xl mx-auto">
          {steps.map((step, index) => (
            <div 
              key={step.number} 
              className="step-card animate-fade-in-up opacity-0"
              style={{ animationDelay: `${0.1 + index * 0.1}s`, animationFillMode: 'forwards' }}
              data-testid={`step-${step.number}`}
            >
              <div className="step-number">{step.number}</div>
              <p className="font-body text-dark-burgundy" style={{ fontSize: '17px', lineHeight: '1.6' }}>
                {step.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ Waitlist Section ============
const WaitlistSection = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    city: '',
    church: '',
    receive_updates: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formStarted, setFormStarted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (!formStarted) {
      setFormStarted(true);
      trackEvent('form_start', { element_id: 'waitlist-form' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name.trim() || !formData.email.trim()) {
      toast.error('Please fill in your name and email');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/waitlist`, formData);
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success(response.data.message);
        trackEvent('form_submit', { element_id: 'waitlist-form' });
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(message);
      trackEvent('form_error', { element_id: 'waitlist-form', error: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Track form abandonment
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (formStarted && !isSubmitted) {
        trackEvent('form_abandon', { element_id: 'waitlist-form' });
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formStarted, isSubmitted]);

  return (
    <section className="waitlist-section" id="waitlist" data-testid="waitlist-section">
      <div className="max-w-xl mx-auto relative z-10">
        <h2 className="font-display text-cream text-center mb-10" style={{ fontSize: '32px' }} data-testid="waitlist-heading">
          Sign Up for Early Access
        </h2>
        
        {isSubmitted ? (
          <div className="success-message animate-fade-in-up" data-testid="success-message">
            <div className="w-16 h-16 mx-auto mb-4 bg-cream/20 rounded-full flex items-center justify-center">
              <Check className="w-8 h-8 text-cream" />
            </div>
            <p className="font-body text-cream text-lg mb-6">
              Thank you! You're on the Yoked waitlist. We'll notify you when early access opens.
            </p>
            
            {/* Social Share Buttons */}
            <div className="mt-8 pt-6 border-t border-cream/20">
              <p className="font-body text-cream/80 text-sm mb-4 flex items-center justify-center gap-2">
                <Share2 className="w-4 h-4" />
                Help spread the word — share with friends who share your faith
              </p>
              <div className="flex items-center justify-center gap-3">
                {/* WhatsApp Share */}
                <a
                  href={`https://wa.me/?text=${encodeURIComponent("I just joined the Yoked waitlist — a faith-focused Christian courtship platform for believers seeking intentional, Christ-centred relationships. Join me! " + window.location.origin)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-btn whatsapp"
                  onClick={() => trackEvent('social_share', { platform: 'whatsapp' })}
                  data-testid="share-whatsapp"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span>WhatsApp</span>
                </a>
                
                {/* Instagram - Copy Link */}
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.origin + "?ref=instagram");
                    toast.success("Link copied! Share it on Instagram");
                    trackEvent('social_share', { platform: 'instagram' });
                  }}
                  className="share-btn instagram"
                  data-testid="share-instagram"
                >
                  <Instagram className="w-5 h-5" />
                  <span>Instagram</span>
                </button>
                
                {/* Copy Link */}
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.origin);
                    toast.success("Link copied to clipboard!");
                    trackEvent('social_share', { platform: 'copy_link' });
                  }}
                  className="share-btn copy-link"
                  data-testid="share-copy"
                >
                  <Copy className="w-5 h-5" />
                  <span>Copy Link</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="waitlist-form">
            <div>
              <input type="text" name="full_name" value={formData.full_name} onChange={handleChange}
                placeholder="Full Name *" className="form-input" required data-testid="input-full-name" />
            </div>
            <div>
              <input type="email" name="email" value={formData.email} onChange={handleChange}
                placeholder="Email Address *" className="form-input" required data-testid="input-email" />
            </div>
            <div>
              <input type="text" name="city" value={formData.city} onChange={handleChange}
                placeholder="City / Country (Optional)" className="form-input" data-testid="input-city" />
            </div>
            <div>
              <input type="text" name="church" value={formData.church} onChange={handleChange}
                placeholder="Church / Denomination (Optional)" className="form-input" data-testid="input-church" />
            </div>
            <div className="flex items-center gap-3 pt-2">
              <input type="checkbox" name="receive_updates" checked={formData.receive_updates} onChange={handleChange}
                className="custom-checkbox" id="receive_updates" data-testid="checkbox-updates" />
              <label htmlFor="receive_updates" className="font-body text-cream text-sm cursor-pointer">
                I want to receive updates about Yoked
              </label>
            </div>
            <button type="submit" className="btn-secondary w-full mt-6 flex items-center justify-center gap-2"
              disabled={isSubmitting} data-testid="submit-btn">
              {isSubmitting ? (<><Loader2 className="w-5 h-5 animate-spin" />Joining...</>) : 'Join the Waitlist'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

// ============ Why Section ============
const WhySection = () => {
  const reasons = [
    { icon: <Users className="w-6 h-6" />, title: "Faith-Focused Community", description: "Only faith-focused, intentional Christian singles" },
    { icon: <Shield className="w-6 h-6" />, title: "Safety & Integrity", description: "Safety and integrity in every profile" },
    { icon: <Heart className="w-6 h-6" />, title: "Marriage-Minded", description: "Focus on meaningful, marriage-minded relationships" },
    { icon: <Church className="w-6 h-6" />, title: "Kingdom-Aligned", description: "Build a kingdom-aligned future with your partner" }
  ];

  return (
    <section className="why-section" id="why" data-testid="why-section">
      <div className="max-w-5xl mx-auto">
        <h2 className="font-display section-heading text-burgundy text-center mb-12" style={{ fontSize: '36px' }} data-testid="why-heading">
          Why Yoked?
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {reasons.map((reason, index) => (
            <div key={index} className="why-card animate-fade-in-up opacity-0"
              style={{ animationDelay: `${0.1 + index * 0.1}s`, animationFillMode: 'forwards' }} data-testid={`why-card-${index}`}>
              <div className="why-icon">{reason.icon}</div>
              <div>
                <h3 className="font-display text-burgundy mb-2" style={{ fontSize: '20px' }}>{reason.title}</h3>
                <p className="font-body text-dark-burgundy" style={{ fontSize: '15px' }}>{reason.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ Footer ============
const FooterSection = () => (
  <footer className="footer-section py-12" data-testid="footer-section">
    <div className="max-w-5xl mx-auto px-6">
      <div className="flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="flex items-center gap-3">
          <InfinityLogo />
          <span className="font-cta text-cream text-xl font-semibold tracking-wide">YOKED</span>
        </div>
        <div className="flex items-center gap-4">
          <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-icon" data-testid="social-instagram">
            <Instagram className="w-5 h-5" />
          </a>
          <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-icon" data-testid="social-facebook">
            <Facebook className="w-5 h-5" />
          </a>
          <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="social-icon" data-testid="social-linkedin">
            <Linkedin className="w-5 h-5" />
          </a>
        </div>
      </div>
      <div className="border-t border-cream/20 mt-8 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-cream/80 font-body text-sm">
          <Mail className="w-4 h-4" />
          <a href="mailto:info@yokedapp.com" className="hover:text-cream transition-colors" data-testid="email-link">info@yokedapp.com</a>
        </div>
        <div className="flex items-center gap-6 font-body text-sm text-cream/60">
          <a href="#" className="hover:text-cream transition-colors" data-testid="privacy-link">Privacy Policy</a>
          <span>© 2025 Yoked. All rights reserved.</span>
        </div>
      </div>
    </div>
  </footer>
);

// ============ Landing Page ============
const LandingPage = () => {
  useEffect(() => {
    trackEvent('page_view', { page: '/' });
    
    // Track scroll depth
    let maxScroll = 0;
    const handleScroll = () => {
      const scrollPercent = Math.round((window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100);
      if (scrollPercent > maxScroll && scrollPercent % 25 === 0) {
        maxScroll = scrollPercent;
        trackEvent('scroll_depth', { scroll_depth: scrollPercent });
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div data-testid="landing-page">
      <Header />
      <main>
        <HeroSection />
        <AboutSection />
        <WaitlistSection />
        <WhySection />
      </main>
      <FooterSection />
    </div>
  );
};

// ============ Admin Login ============
const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/admin');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(e => e.msg).join(' '));
      else setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <InfinityLogo className="mx-auto mb-4" />
          <h1 className="font-display text-burgundy text-3xl">Admin Login</h1>
          <p className="font-body text-dark-burgundy mt-2">Access your dashboard</p>
        </div>
        
        <form onSubmit={handleSubmit} className="bg-white rounded-xl p-8 shadow-lg border border-burgundy/10">
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm" data-testid="login-error">
              {error}
            </div>
          )}
          
          <div className="mb-4">
            <label className="block font-body text-dark-burgundy text-sm mb-2">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-burgundy/20 focus:outline-none focus:border-burgundy"
              placeholder="admin@yokedapp.com" required data-testid="login-email" />
          </div>
          
          <div className="mb-6">
            <label className="block font-body text-dark-burgundy text-sm mb-2">Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-burgundy/20 focus:outline-none focus:border-burgundy"
              placeholder="••••••••" required data-testid="login-password" />
          </div>
          
          <button type="submit" disabled={loading}
            className="btn-primary w-full flex items-center justify-center gap-2" data-testid="login-submit">
            {loading ? (<><Loader2 className="w-5 h-5 animate-spin" />Logging in...</>) : 'Login'}
          </button>
        </form>
        
        <p className="text-center mt-6">
          <a href="/" className="text-burgundy hover:underline font-body text-sm">← Back to Landing Page</a>
        </p>
      </div>
    </div>
  );
};

// ============ Admin Dashboard ============
const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('waitlist');
  const [waitlistData, setWaitlistData] = useState({ entries: [], total: 0, page: 1, pages: 1 });
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadData();
  }, [currentPage, activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'waitlist') {
        const response = await axios.get(`${API}/admin/waitlist?page=${currentPage}&limit=10`);
        setWaitlistData(response.data);
      } else {
        const response = await axios.get(`${API}/admin/analytics/summary?days=7`);
        setAnalytics(response.data);
      }
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/admin/waitlist/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'yoked_waitlist.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded');
    } catch {
      toast.error('Export failed');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API}/admin/waitlist/${entryId}`);
      toast.success('Entry deleted');
      loadData();
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-burgundy text-cream py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <InfinityLogo />
            <span className="font-cta text-xl font-semibold">YOKED Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-body text-sm">{user?.email}</span>
            <button onClick={handleLogout} className="flex items-center gap-2 text-cream/80 hover:text-cream transition-colors" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto p-6">
        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button onClick={() => setActiveTab('waitlist')}
            className={`px-6 py-3 rounded-lg font-body text-sm transition-all ${activeTab === 'waitlist' ? 'bg-burgundy text-cream' : 'bg-white text-burgundy border border-burgundy/20'}`}
            data-testid="tab-waitlist">
            <Users2 className="w-4 h-4 inline mr-2" />Waitlist
          </button>
          <button onClick={() => setActiveTab('analytics')}
            className={`px-6 py-3 rounded-lg font-body text-sm transition-all ${activeTab === 'analytics' ? 'bg-burgundy text-cream' : 'bg-white text-burgundy border border-burgundy/20'}`}
            data-testid="tab-analytics">
            <BarChart3 className="w-4 h-4 inline mr-2" />Analytics
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-burgundy" />
          </div>
        ) : activeTab === 'waitlist' ? (
          /* Waitlist Tab */
          <div className="bg-white rounded-xl shadow-sm border border-burgundy/10" data-testid="waitlist-table">
            <div className="p-6 border-b border-burgundy/10 flex items-center justify-between">
              <div>
                <h2 className="font-display text-burgundy text-xl">Waitlist Entries</h2>
                <p className="font-body text-dark-burgundy/60 text-sm mt-1">Total: {waitlistData.total} signups</p>
              </div>
              <button onClick={handleExport} className="btn-primary py-2 px-4 text-sm flex items-center gap-2" data-testid="export-btn">
                <Download className="w-4 h-4" />Export CSV
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-cream/50">
                  <tr>
                    <th className="px-6 py-3 text-left font-body text-sm text-dark-burgundy">Name</th>
                    <th className="px-6 py-3 text-left font-body text-sm text-dark-burgundy">Email</th>
                    <th className="px-6 py-3 text-left font-body text-sm text-dark-burgundy">Location</th>
                    <th className="px-6 py-3 text-left font-body text-sm text-dark-burgundy">Church</th>
                    <th className="px-6 py-3 text-left font-body text-sm text-dark-burgundy">Updates</th>
                    <th className="px-6 py-3 text-left font-body text-sm text-dark-burgundy">Date</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {waitlistData.entries.map((entry, idx) => (
                    <tr key={entry.id || idx} className="border-t border-burgundy/5 hover:bg-cream/30" data-testid={`waitlist-row-${idx}`}>
                      <td className="px-6 py-4 font-body text-dark-burgundy">{entry.full_name}</td>
                      <td className="px-6 py-4 font-body text-dark-burgundy">{entry.email}</td>
                      <td className="px-6 py-4 font-body text-dark-burgundy/60">{entry.city || '-'}</td>
                      <td className="px-6 py-4 font-body text-dark-burgundy/60">{entry.church || '-'}</td>
                      <td className="px-6 py-4">
                        {entry.receive_updates ? <Check className="w-4 h-4 text-green-600" /> : <span className="text-dark-burgundy/40">-</span>}
                      </td>
                      <td className="px-6 py-4 font-body text-dark-burgundy/60 text-sm">
                        {new Date(entry.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <button onClick={() => handleDelete(entry.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-btn-${idx}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {waitlistData.pages > 1 && (
              <div className="p-4 border-t border-burgundy/10 flex items-center justify-center gap-4">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-burgundy/20 disabled:opacity-40" data-testid="prev-page">
                  <ChevronLeft className="w-4 h-4 text-burgundy" />
                </button>
                <span className="font-body text-sm text-dark-burgundy">Page {currentPage} of {waitlistData.pages}</span>
                <button onClick={() => setCurrentPage(p => Math.min(waitlistData.pages, p + 1))} disabled={currentPage === waitlistData.pages}
                  className="p-2 rounded-lg border border-burgundy/20 disabled:opacity-40" data-testid="next-page">
                  <ChevronRight className="w-4 h-4 text-burgundy" />
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Analytics Tab */
          <div data-testid="analytics-panel">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl p-6 border border-burgundy/10">
                <div className="flex items-center gap-3 mb-2">
                  <Eye className="w-5 h-5 text-burgundy" />
                  <span className="font-body text-sm text-dark-burgundy/60">Page Views</span>
                </div>
                <p className="font-display text-2xl text-burgundy" data-testid="stat-page-views">{analytics?.page_views || 0}</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-burgundy/10">
                <div className="flex items-center gap-3 mb-2">
                  <Users2 className="w-5 h-5 text-burgundy" />
                  <span className="font-body text-sm text-dark-burgundy/60">Unique Visitors</span>
                </div>
                <p className="font-display text-2xl text-burgundy" data-testid="stat-visitors">{analytics?.unique_sessions || 0}</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-burgundy/10">
                <div className="flex items-center gap-3 mb-2">
                  <MousePointer className="w-5 h-5 text-burgundy" />
                  <span className="font-body text-sm text-dark-burgundy/60">CTA Clicks</span>
                </div>
                <p className="font-display text-2xl text-burgundy" data-testid="stat-clicks">{analytics?.cta_clicks || 0}</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-burgundy/10">
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="w-5 h-5 text-burgundy" />
                  <span className="font-body text-sm text-dark-burgundy/60">Form Submits</span>
                </div>
                <p className="font-display text-2xl text-burgundy" data-testid="stat-submits">{analytics?.form_submissions || 0}</p>
              </div>
            </div>

            {/* More Stats */}
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl p-6 border border-burgundy/10">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className="w-5 h-5 text-burgundy" />
                  <span className="font-body text-sm text-dark-burgundy/60">Conversion Rate</span>
                </div>
                <p className="font-display text-2xl text-burgundy" data-testid="stat-conversion">{analytics?.conversion_rate || 0}%</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-burgundy/10">
                <div className="flex items-center gap-3 mb-2">
                  <BarChart3 className="w-5 h-5 text-burgundy" />
                  <span className="font-body text-sm text-dark-burgundy/60">Avg Scroll Depth</span>
                </div>
                <p className="font-display text-2xl text-burgundy" data-testid="stat-scroll">{analytics?.avg_scroll_depth || 0}%</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-burgundy/10">
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="w-5 h-5 text-red-500" />
                  <span className="font-body text-sm text-dark-burgundy/60">Form Abandons</span>
                </div>
                <p className="font-display text-2xl text-burgundy" data-testid="stat-abandons">{analytics?.form_abandons || 0}</p>
              </div>
            </div>

            {/* Daily Chart */}
            <div className="bg-white rounded-xl p-6 border border-burgundy/10">
              <h3 className="font-display text-burgundy text-lg mb-4">Daily Page Views (Last 7 Days)</h3>
              <div className="flex items-end gap-2 h-40">
                {analytics?.daily_views?.map((day, idx) => (
                  <div key={idx} className="flex-1 flex flex-col items-center">
                    <div className="bg-burgundy rounded-t w-full" 
                      style={{ height: `${Math.max(10, (day.views / Math.max(...analytics.daily_views.map(d => d.views))) * 100)}%` }}
                      title={`${day.views} views`} />
                    <span className="font-body text-xs text-dark-burgundy/60 mt-2">{day.date.slice(5)}</span>
                  </div>
                ))}
                {(!analytics?.daily_views || analytics.daily_views.length === 0) && (
                  <p className="font-body text-dark-burgundy/40 w-full text-center">No data yet</p>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// ============ Main App ============
function App() {
  return (
    <AuthProvider>
      <div className="App" data-testid="app-container">
        <Toaster position="top-center" toastOptions={{
          style: { background: '#800020', color: '#FFF5E1', border: '1px solid rgba(255, 245, 225, 0.2)' }
        }} />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
